import { Box, Tab, Tabs, Typography, CircularProgress, Skeleton, Slide } from '@mui/material';
import PropTypes from 'prop-types';
import './TransactionHistory.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DisputeIcon from '../../../assets/WalletIcon/dispute-icon.svg';
import ReceivedIcon from '../../../assets/WalletIcon/received-icon.svg';
import { useTranslation } from '../../../i18n/LanguageContext';
import { format, parseISO } from 'date-fns';
import { axiosInstance } from '../../../utils/tokenUtils';
import TransactionHistorySkeleton from '../../SkeletonEffect/SkeletonEffect';
// import EmptyStateIcon from "../../../assets/empty-state-icon.svg";

const EarningHistoryMap = {
  session_payment: {
    class: 'received-view-all-icon-container',
    icon: ReceivedIcon,
    amountClass: 'single-view-all-earning-history-received-amount-text',
  },
  withdrawal: {
    class: 'dispute-view-all-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-view-all-earning-history-dispute-amount-text',
  },
  others: {
    class: 'dispute-view-all-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-view-all-earning-history-dispute-amount-text',
  },
  null: {
    class: 'dispute-view-all-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-view-all-earning-history-dispute-amount-text',
  },
};

function CustomTabPanel(props) {
  const { children, value, index, direction, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      aria-label={`${props.label} panel`}
      style={{ position: 'relative', width: '100%', overflow: 'hidden' }}
      {...other}
    >
      <Slide direction={direction} in={value === index} mountOnEnter unmountOnExit>
        <Box sx={{ p: 3 }}>{children}</Box>
      </Slide>
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  direction: PropTypes.oneOf(['left', 'right', 'up', 'down']),
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// Format transaction data
const formatTransactionData = transactions => {
  return transactions.map(transaction => ({
    ...transaction,
    // Format koins to 2 decimal places
    koins: parseFloat(transaction.koins).toFixed(2),
    // Format title to be more readable
    title: transaction.title
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' '),
    // Format transaction type to be more readable
    transaction_type: transaction.transaction_type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' '),
  }));
};

const TransactionHistory = () => {
  const [value, setValue] = useState(0);
  const [prevValue, setPrevValue] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');
  const { translate } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVerified, setIsVerified] = useState(true);
  const [pagination, setPagination] = useState({
    count: 0,
    offset: 0,
    limit: 25,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef(null);

  const fetchTransactionHistory = useCallback(
    async (isLoadMore = false) => {
      try {
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        const currentOffset = isLoadMore ? pagination.offset + pagination.limit : 0;

        const response = await axiosInstance.get('/teachers/transaction-history', {
          params: {
            limit: pagination.limit,
            offset: currentOffset,
          },
        });

        if (response.data && response.data.results && Array.isArray(response.data.results)) {
          // Format the transaction data before setting it
          const formattedData = formatTransactionData(response.data.results);

          if (isLoadMore) {
            // Append new data to existing transactions
            setTransactions(prev => [...prev, ...formattedData]);
          } else {
            // Replace existing data
            setTransactions(formattedData);
          }

          setPagination({
            count: response.data.count || 0,
            offset: currentOffset,
            limit: response.data.limit || 25,
          });

          // Check if there are more records to load
          const totalLoaded = isLoadMore
            ? transactions.length + formattedData.length
            : formattedData.length;
          setHasMore(totalLoaded < (response.data.count || 0));

          setIsVerified(true);
        } else if (
          response.data &&
          response.data.message &&
          response.data.message.includes('verified')
        ) {
          // Handle "only verified users can view transaction history"
          setIsVerified(false);
        } else {
          if (!isLoadMore) {
            setTransactions([]);
          }
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching transaction history:', err);
        setError('Failed to load transaction history');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pagination.limit, pagination.offset, transactions.length]
  );

  useEffect(() => {
    fetchTransactionHistory(false);
  }, []);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when user scrolls to 80% of the content
    if (scrollPercentage > 0.8) {
      fetchTransactionHistory(true);
    }
  }, [loadingMore, hasMore, fetchTransactionHistory]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Group transactions by date
  const groupTransactionsByDate = transactionList => {
    const grouped = {};

    transactionList.forEach(transaction => {
      // Parse the date from the created_at field
      const date = format(parseISO(transaction.created_at), 'dd MMMM yyyy');

      if (!grouped[date]) {
        grouped[date] = [];
      }

      grouped[date].push(transaction);
    });

    return grouped;
  };

  // Filter transactions by type
  const filterTransactions = type => {
    if (type === 'all') return transactions;

    if (type === 'received') {
      return transactions.filter(t => t.transaction_type === 'Session Payment');
    } else if (type === 'withdrawal') {
      return transactions.filter(t => t.transaction_type === 'Withdrawal');
    } else if (type === 'other') {
      // Include non-received and non-withdrawal in "other" category (dispute, refund, cancellations)
      return transactions.filter(
        t => t.transaction_type !== 'Session Payment' && t.transaction_type !== 'Withdrawal'
      );
    }

    return [];
  };

  const handleChange = (event, newValue) => {
    // Determine slide direction based on tab index change
    setSlideDirection(newValue > value ? 'left' : 'right');
    setPrevValue(value);
    setValue(newValue);
  };

  // Determine transaction type for display
  const getTransactionType = transaction => {
    if (!transaction.transaction_type) return 'null';
    // Convert formatted transaction type back to lowercase for mapping
    return transaction.transaction_type.toLowerCase().replace(/\s+/g, '_');
  };

  // Render empty state
  const renderEmptyState = message => (
    <Box className="flex flex-col items-center justify-center py-8">
      {/* <img src={EmptyStateIcon} alt="Empty state" className="mb-4" /> */}
      <Typography className="text-gray-500">{message}</Typography>
    </Box>
  );

  // Render skeleton loading animation
  const renderSkeletonLoading = () => <TransactionHistorySkeleton count={5} />;

  // Render loading more skeleton
  const renderLoadingMore = () => (
    <Box className="flex justify-center py-4">
      <TransactionHistorySkeleton count={3} />
    </Box>
  );

  // Render transactions for a specific tab
  const renderTransactions = transactionType => {
    const filteredTransactions = filterTransactions(transactionType);

    if (filteredTransactions.length === 0) {
      return renderEmptyState('No transactions done yet');
    }

    const groupedTransactions = groupTransactionsByDate(filteredTransactions);

    return (
      <Box className="flex flex-col gap-4">
        {Object.entries(groupedTransactions).map(([date, dateTransactions]) => (
          <Box key={date} className="flex flex-col gap-4">
            <div className="transaction-date-header">{date}</div>
            <Box className="flex flex-col gap-4">
              {dateTransactions.map(item => {
                const type = getTransactionType(item);
                return (
                  <Box className="single-view-all-earning-history-container" key={item.id}>
                    <Box className="flex items-center gap-4">
                      <Box
                        className={
                          EarningHistoryMap[type]?.class || EarningHistoryMap['null'].class
                        }
                      >
                        <img
                          src={EarningHistoryMap[type]?.icon || EarningHistoryMap['null'].icon}
                          alt={type}
                        />
                      </Box>
                      <Box className="flex flex-col gap-1">
                        <div className="single-view-all-earning-history-title-text">
                          {item.transaction_type}
                        </div>
                        <div className="single-view-all-earning-history-date-text">
                          {format(parseISO(item.created_at), 'dd MMMM yyyy, h:mm a')}
                        </div>
                      </Box>
                    </Box>
                    <div
                      className={
                        EarningHistoryMap[type]?.amountClass ||
                        EarningHistoryMap['null'].amountClass
                      }
                    >
                      {type === 'session_payment' ? '+' : '-'} {item.koins}
                    </div>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      data-testid="teacher-session-component"
      className="h-[calc(100vh-295px)] relative bg-[#FFF] rounded-lg flex flex-col"
    >
      {/* Fixed Tabs Section */}
      <Box sx={{ width: '100%', flexShrink: 0 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            background: '#FFF',
            margin: '15px 15px 0px 15px',
          }}
        >
          <Tabs value={value} onChange={handleChange} aria-label="transaction history tabs">
            <Tab
              label={translate('wallet', 'allText')}
              sx={{ textTransform: 'none' }}
              {...a11yProps(0)}
            />
            <Tab
              label={translate('wallet', 'receivedText')}
              sx={{ textTransform: 'none' }}
              {...a11yProps(1)}
            />
            <Tab
              label={translate('wallet', 'withdrawalText') || 'Withdrawal'}
              sx={{ textTransform: 'none' }}
              {...a11yProps(2)}
            />
            <Tab
              label={translate('wallet', 'otherText')}
              sx={{ textTransform: 'none' }}
              {...a11yProps(3)}
            />
          </Tabs>
        </Box>
      </Box>

      {/* Scrollable Content Section */}
      <Box ref={scrollContainerRef} className="flex-1 overflow-auto">
        {loading || error ? (
          // Show skeleton loading for both loading and error states
          <CustomTabPanel value={value} index={value} direction={slideDirection}>
            {renderSkeletonLoading()}
          </CustomTabPanel>
        ) : !isVerified ? (
          renderEmptyState(translate('wallet', 'userNotVerifiedText'))
        ) : (
          <>
            <CustomTabPanel value={value} index={0} direction={slideDirection}>
              {renderTransactions('all')}
              {loadingMore && renderLoadingMore()}
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1} direction={slideDirection}>
              {renderTransactions('received')}
              {loadingMore && renderLoadingMore()}
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2} direction={slideDirection}>
              {renderTransactions('withdrawal')}
              {loadingMore && renderLoadingMore()}
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3} direction={slideDirection}>
              {renderTransactions('other')}
              {loadingMore && renderLoadingMore()}
            </CustomTabPanel>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TransactionHistory;
